import React, { type ComponentProps, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { type IconName, ListActionPressable, useToggleState } from '@sg/garnish';
import { type CardType, type PaymentMethodCard } from '@sg/graphql-schema';

import { CreditCardForm } from '@order/integrations/stripe';

import { Modal, SelectModal, useApplePayPaymentMethod } from '..';
import { APPLE_PAY, getCreditCardIcon, PAYMENT_ADD } from '../../constants';

/**
 * Payment method selector.
 * Rendered as a Modal on all viewports.
 */
export const PaymentMethodSelectorModal = (props: PaymentMethodSelectorProps) => {
  const {
    selectedPaymentMethodId,
    selectedPaymentMethodLabel,
    selectedPaymentMethodIcon,
    paymentMethodOptions,
    paymentMethodSelectionNotice,
    paymentMethodSelectionNoticePalette,
    hasBoldLabel,
    onPaymentMethodSelected,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const options = useSelectModalOptions(paymentMethodOptions);

  // ─── Apple Pay ───────────────────────────────────────────────────────

  const { updateDefaultPaymentMethodToApplePay } = useApplePayPaymentMethod();

  // ─── Callbacks ───────────────────────────────────────────────────────

  const {
    value: isShowingAddPaymentMethodModal,
    toggleOn: showAddPaymentMethodModal,
    toggleOff: hideAddPaymentMethodModal,
  } = useToggleState();

  const {
    value: isShowingSwitchPaymentMethodModal,
    toggleOn: showSwitchPaymentMethodModal,
    toggleOff: hideSwitchPaymentMethodModal,
  } = useToggleState();

  const handleSwitchPaymentMethod = useCallback(
    (id: string) => {
      const hasChosenAddPaymentMethod = id === PAYMENT_ADD;

      if (hasChosenAddPaymentMethod) {
        // NOTE: We use a short delay on iOS because of a known issue during interactions between two modals.
        const delay = Platform.select({ ios: 500, default: 0 });

        setTimeout(showAddPaymentMethodModal, delay);

        return;
      }

      const paymentMethod = paymentMethodOptions.find((option) => option.value === id);

      if (!paymentMethod) return;

      onPaymentMethodSelected(paymentMethod.value);

      if (id === APPLE_PAY) void updateDefaultPaymentMethodToApplePay();
    },
    [
      paymentMethodOptions,
      onPaymentMethodSelected,
      updateDefaultPaymentMethodToApplePay,
      showAddPaymentMethodModal,
    ],
  );

  const handlePaymentMethodAdded = useCallback(
    (paymentMethod: PaymentMethodCard) => {
      onPaymentMethodSelected(paymentMethod.id);
      hideAddPaymentMethodModal();
    },
    [onPaymentMethodSelected, hideAddPaymentMethodModal],
  );

  const handleSelectorPressed = useCallback(() => {
    const shouldOpenCreditCardAddModal = paymentMethodOptions.length === 1;

    if (shouldOpenCreditCardAddModal) {
      showAddPaymentMethodModal();

      return;
    }

    showSwitchPaymentMethodModal();
  }, [paymentMethodOptions.length, showSwitchPaymentMethodModal, showAddPaymentMethodModal]);

  return (
    <>
      <ListActionPressable
        withCaret
        boldTitle={hasBoldLabel}
        accessibilityLabel={formatMessage(messages.checkoutSelectPaymentMethod)}
        title={selectedPaymentMethodLabel}
        iconName={selectedPaymentMethodIcon}
        notice={paymentMethodSelectionNotice}
        noticePalette={paymentMethodSelectionNoticePalette}
        onPress={handleSelectorPressed}
      />

      <SelectModal
        title={formatMessage(messages.checkoutSelectPaymentMethod)}
        selectedOption={selectedPaymentMethodId}
        options={options}
        visible={isShowingSwitchPaymentMethodModal}
        onOptionSelected={handleSwitchPaymentMethod}
        onRequestClose={hideSwitchPaymentMethodModal}
      />

      <CreditCardForm.AddModal
        Modal={Modal}
        visible={isShowingAddPaymentMethodModal}
        onRequestClose={hideAddPaymentMethodModal}
        onSuccess={handlePaymentMethodAdded}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  checkoutSelectPaymentMethod: {
    defaultMessage: 'Select payment method',
    description: 'Checkout | Select Payment Method',
  },
  checkoutAddNewPaymentMethod: {
    defaultMessage: 'Add new payment method',
    description: 'Checkout | Add New Payment Method',
  },
});

// ─── Helpers ───────────────────────────────────────────────────────────

function useSelectModalOptions(
  paymentMethodOptions: Readonly<CreditCardOption[]>,
): Readonly<ComponentProps<typeof SelectModal>['options']> {
  const { formatMessage } = useIntl();
  const addLabel = formatMessage(messages.checkoutAddNewPaymentMethod);
  const shouldUseAddIcon = true;

  return useMemo(
    () =>
      [...paymentMethodOptions].reverse().map((option) => ({
        value: option.value,
        label: option.value === PAYMENT_ADD ? addLabel : option.label,
        icon: getCreditCardIcon(option.cardType, shouldUseAddIcon),
      })),
    [paymentMethodOptions, shouldUseAddIcon, addLabel],
  );
}

// ─── Types ─────────────────────────────────────────────────────────────

type PaymentMethodSelectorProps = Readonly<{
  selectedPaymentMethodId: string | undefined;
  selectedPaymentMethodLabel: string;
  paymentMethodOptions: Readonly<CreditCardOption[]>;
  selectedPaymentMethodIcon: IconName;
  paymentMethodSelectionNotice?: string;
  paymentMethodSelectionNoticePalette?: ComponentProps<typeof ListActionPressable>['noticePalette'];
  hasBoldLabel?: boolean;
  onPaymentMethodSelected: (paymentMethodId: string) => void;
}>;

type CreditCardOption = Readonly<{
  value: string;
  label: string;
  cardType: CardType | typeof PAYMENT_ADD | typeof APPLE_PAY;
  paymentMethodType: 'BillingAccount' | 'PaymentMethodCard' | 'PaymentMethodBillingAccount';
  isDefault: boolean;
}>;
